'use client';

/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useIdleTimer } from 'react-idle-timer';

// Account
import useAccount from '@features/Account/store/useAccount';
import useViewAs from '@shared/providers/ViewAsProvider/useViewAs';
import { setViewAsUser } from '@shared/providers/ViewAsProvider/store';
import { isSpecialUserEmail } from '@shared/constants/users';

// API & types
import { useRefreshOnlineStatusMutation } from '@app/types/blackroom-api';
import { UserActivityProviderProps } from './types';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

function UserActivityProvider({
  children,
  ...props
}: UserActivityProviderProps) {
  /* ********************************** HOOKS ********************************* */

  // API
  const [refreshOnlineStatus] = useRefreshOnlineStatusMutation();

  // Auth
  const { account } = useAccount();
  const { logout } = useAuth0();
  const { isViewingAs } = useViewAs();
  const isSpecialUser = isSpecialUserEmail(account?.email);

  const onIdle = () => {
    setViewAsUser(null);
    logout({
      logoutParams: { returnTo: window.location.origin },
    });
  };

  useIdleTimer({
    onIdle,
    timeout:
      Number(import.meta.env.VITE_APP_USERS_IDLE_TIME_SECONDS ?? 7200) * 1000,
  });

  // Side-effects
  useEffect(() => {
    if (isViewingAs) return;
    if (isSpecialUser) return;

    const intervalId = setInterval(
      () => {
        if (account) {
          refreshOnlineStatus();
        }
      },
      Number(import.meta.env.VITE_APP_USERS_REFRESH_ONLINE_RATE_S ?? 20) * 1000,
    );

    return () => clearInterval(intervalId);
  }, [account, isViewingAs, isSpecialUser]);

  /* ******************************** RENDERING ******************************* */

  return <>{children}</>;
}

export default UserActivityProvider;
